"use client";

import * as React from "react";
import { Maximize2 } from "lucide-react";
import type { CellState, CellValue, TableColumn, TableRow as TableRowData } from "@quovy/types";
import { EditableCell } from "./cells/editable-cell";
import type { CellMenuActions } from "./cells/cell-menu";
import { cn } from "../../utils";
import { Button } from "../../inputs/button";
import { Checkbox } from "../../primitives/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../primitives/tooltip";
import { RowMenu, type RowMenuActions } from "./row-menu";

export interface TableRowProps {
  row: TableRowData;
  fields: TableColumn[];
  /** Cell state keyed by `columnId`. Missing keys render as empty cells. */
  cells: Record<string, CellState>;
  selected?: boolean;
  hovered?: boolean;
  /** Field id of the currently focused cell on this row. */
  focusedColumnId?: string;
  /** Field id of the cell currently in edit mode. */
  editingColumnId?: string;
  onToggleSelected?: (next: boolean) => void;
  onShiftSelect?: () => void;
  /** When provided, render an Airtable-style expand affordance on row hover. */
  onExpandRow?: () => void;
  onStartEdit?: (columnId: string) => void;
  onStopEdit?: (columnId: string) => void;
  onCommitEdit?: (columnId: string, next: CellValue) => void | Promise<void>;
  /** See `EditableCellProps.onCitationSelect`. */
  onCitationSelect?: (columnId: string, citationId: string) => void;
  /** See `EditableCellProps.onCitationView`. */
  onCitationView?: (columnId: string, citationId: string) => void;
  onOpenFileViewer?: (
    rowId: string,
    opts?: { columnId?: string; initialTab?: "citations" | "history" | "validation" },
  ) => void;
  buildCellContextActions?: (field: TableColumn, cell: CellState) => CellMenuActions | undefined;
  rowContextActions?: RowMenuActions;
  /** Force the row context menu open (for stories). */
  defaultRowContextMenuOpen?: boolean;
  /** When true, render the in-cell citation affordance next to the value (if citations exist). */
  showCitationBadge?: boolean;
  className?: string;
}

/**
 * A single grid row. Selection checkbox first, then one `EditableCell` per field
 * in `fields` order. Wraps everything in a row-level context menu.
 */
function TableRowInner({
  row,
  fields,
  cells,
  selected,
  hovered,
  focusedColumnId,
  editingColumnId,
  onToggleSelected,
  onShiftSelect,
  onExpandRow,
  onStartEdit,
  onStopEdit,
  onCommitEdit,
  onCitationSelect,
  onCitationView,
  onOpenFileViewer,
  buildCellContextActions,
  rowContextActions,
  defaultRowContextMenuOpen,
  showCitationBadge,
  className,
}: TableRowProps) {
  const body = (
    <div
      role="row"
      data-slot="grid-row"
      data-selected={selected ? "true" : undefined}
      data-hovered={hovered ? "true" : undefined}
      className={cn(
        "group/row flex items-stretch border-b border-border",
        hovered && "bg-muted/40",
        selected && "bg-primary/5",
        "hover:bg-muted/40",
        className,
      )}
    >
      <div
        className={cn(
          "sticky left-0 z-10 flex w-20 shrink-0 items-center gap-1 border-r border-border bg-background pl-3 pr-2",
          selected && "bg-primary/5",
        )}
      >
        <div
          data-slot="grid-selection-checkbox-cell"
          className="flex items-center"
          onClick={(e) => {
            if (e.shiftKey && onShiftSelect) {
              e.preventDefault();
              onShiftSelect();
            }
          }}
        >
          <Checkbox
            aria-label={`Select row ${row.anchorSummary}`}
            checked={!!selected}
            onCheckedChange={(v) => onToggleSelected?.(v === true)}
          />
        </div>
        {onExpandRow && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Expand row ${row.anchorSummary ?? ""}`.trim()}
                onClick={(e) => {
                  e.stopPropagation();
                  onExpandRow();
                }}
                className="size-6 shrink-0 border border-border bg-background text-muted-foreground opacity-0 shadow-sm hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-primary/40 group-hover/row:opacity-100"
              >
                <Maximize2 className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Expand</TooltipContent>
          </Tooltip>
        )}
      </div>
      {fields.map((field) => {
        const cell: CellState = cells[field.id] ?? {
          columnId: field.id,
          value: null,
          citations: [],
          ...(field.source ? { source: field.source } : {}),
        };
        const isFocused = focusedColumnId === field.id;
        const isEditing = editingColumnId === field.id;
        const contextActions = buildCellContextActions?.(field, cell);
        return (
          <div
            key={field.id}
            role="cell"
            data-pinned={field.pinned ?? undefined}
            style={{ width: field.width }}
            className={cn(
              "flex shrink-0 items-stretch border-r border-border",
              field.pinned === "left" &&
                "sticky left-20 z-10 bg-background shadow-[1px_0_0_0_var(--border)]",
              field.pinned === "right" &&
                "sticky right-0 z-10 bg-background shadow-[-1px_0_0_0_var(--border)]",
              selected && (field.pinned ? "bg-primary/5" : ""),
            )}
          >
            <EditableCell
              field={field}
              cell={cell}
              rowId={row.id}
              {...(isFocused ? { focused: true } : {})}
              {...(isEditing ? { editing: true } : {})}
              {...(selected ? { selected: true } : {})}
              {...(onStartEdit ? { onStartEdit: () => onStartEdit(field.id) } : {})}
              {...(onStopEdit ? { onStopEdit: () => onStopEdit(field.id) } : {})}
              {...(onCommitEdit ? { onCommitEdit: (next) => onCommitEdit(field.id, next) } : {})}
              {...(onCitationSelect
                ? {
                    onCitationSelect: (citationId: string) =>
                      onCitationSelect(field.id, citationId),
                  }
                : {})}
              {...(onCitationView
                ? {
                    onCitationView: (citationId: string) => onCitationView(field.id, citationId),
                  }
                : {})}
              {...(onOpenFileViewer ? { onOpenFileViewer } : {})}
              {...(contextActions ? { contextActions } : {})}
              {...(showCitationBadge !== undefined ? { showCitationBadge } : {})}
            />
          </div>
        );
      })}
    </div>
  );

  if (!rowContextActions) return body;
  return (
    <RowMenu {...rowContextActions} {...(defaultRowContextMenuOpen ? { defaultOpen: true } : {})}>
      {body}
    </RowMenu>
  );
}

/**
 * Hot-path equality: re-render only when row state, the per-row `cells`
 * map, or interaction-bearing primitives change. Handler identity is
 * intentionally ignored — handlers are recreated per render in the body
 * but always do the same thing when row + cells are unchanged.
 */
function tableRowPropsEqual(prev: TableRowProps, next: TableRowProps): boolean {
  return (
    prev.row === next.row &&
    prev.cells === next.cells &&
    prev.fields === next.fields &&
    prev.selected === next.selected &&
    prev.hovered === next.hovered &&
    prev.focusedColumnId === next.focusedColumnId &&
    prev.editingColumnId === next.editingColumnId &&
    prev.className === next.className &&
    Boolean(prev.onExpandRow) === Boolean(next.onExpandRow) &&
    prev.rowContextActions === next.rowContextActions &&
    prev.defaultRowContextMenuOpen === next.defaultRowContextMenuOpen &&
    prev.showCitationBadge === next.showCitationBadge
  );
}

export const TableRow = React.memo(TableRowInner, tableRowPropsEqual);
